import { useEffect, useState } from "react";
import { AlertTriangle, Check, CheckCheck, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATEGORIES = [
    { id: "reminder", label: "Reminders", tid: "tab-reminder" },
    { id: "appointment", label: "Appointments", tid: "tab-appointment" },
    { id: "booking", label: "Booking Requests", tid: "tab-booking" },
    { id: "admin", label: "Admin (PIN)", tid: "tab-admin" },
];

const formatDT = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const StatusTick = ({ label, at, doubleTick = false, testid }) => {
    if (at) {
        return (
            <div className="flex items-start gap-2" data-testid={testid}>
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold shrink-0">
                    {doubleTick ? <CheckCheck size={16} /> : <Check size={16} />}
                    <span className="text-xs uppercase tracking-wide">{label}</span>
                </span>
                <span className="text-xs text-neutral-500 font-mono leading-4">
                    {formatDT(at)}
                </span>
            </div>
        );
    }
    return (
        <div
            className="flex items-center gap-2 text-neutral-400"
            data-testid={`${testid}-pending`}
        >
            <span className="inline-block h-4 w-4 border border-neutral-300" />
            <span className="text-xs uppercase tracking-wide">{label} — not yet</span>
        </div>
    );
};

const EmailsSent = () => {
    const [category, setCategory] = useState("reminder");
    const [data, setData] = useState({ items: [], counts: {}, total: 0, webhook_configured: false });
    const [loading, setLoading] = useState(false);

    const load = (cat = category) => {
        setLoading(true);
        api
            .get(`/email-logs?category=${cat}&limit=200`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load emails"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load(category);
    }, [category]);

    const items = data.items || [];
    const counts = data.counts || {};

    return (
        <div className="space-y-6" data-testid="emails-sent-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-label" style={{ color: "var(--dh-muted)" }}>
                        Delivery tracking
                    </p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
                        Emails sent
                    </h2>
                    <p className="text-sm text-neutral-600 mt-2 max-w-xl">
                        Every email sent from Dragon Heat is logged here. Ticks update
                        live as recipients&rsquo; mail servers deliver, and again when
                        they open the email.
                    </p>
                </div>
                <button
                    data-testid="refresh-emails-btn"
                    onClick={() => load(category)}
                    className="dh-btn"
                    data-variant="ghost"
                >
                    <RefreshCw size={16} /> Refresh
                </button>
            </div>

            {!data.webhook_configured && (
                <div
                    data-testid="webhook-warning"
                    className="border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3"
                >
                    <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <p className="font-bold">
                            Resend webhook not configured yet
                        </p>
                        <p className="mt-1">
                            The list shows &ldquo;sent&rdquo; entries. Delivery /
                            opened ticks will only start appearing once the Resend
                            webhook signing secret is added to the backend and the
                            webhook endpoint is registered in Resend.
                        </p>
                    </div>
                </div>
            )}

            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 border-b-2 border-[color:var(--dh-line)]">
                {CATEGORIES.map((c) => {
                    const active = category === c.id;
                    return (
                        <button
                            key={c.id}
                            data-testid={c.tid}
                            onClick={() => setCategory(c.id)}
                            className={`font-display uppercase font-bold tracking-wide px-4 py-2 border-b-4 -mb-[2px] ${
                                active
                                    ? "border-[color:var(--dh-orange)] text-black"
                                    : "border-transparent text-neutral-500 hover:text-black"
                            }`}
                        >
                            {c.label}
                            <span
                                className="ml-2 inline-block text-xs font-mono px-2 py-0.5 border border-neutral-300 text-neutral-600"
                                data-testid={`${c.tid}-count`}
                            >
                                {counts[c.id] || 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <p className="text-neutral-500" data-testid="emails-loading">
                    Loading…
                </p>
            ) : items.length === 0 ? (
                <div
                    className="border-2 border-dashed border-neutral-300 p-12 text-center"
                    data-testid="emails-empty"
                >
                    <Mail
                        size={32}
                        className="mx-auto text-neutral-400 mb-3"
                    />
                    <p className="text-neutral-600 font-medium">
                        No {CATEGORIES.find((c) => c.id === category)?.label.toLowerCase()} emails yet.
                    </p>
                </div>
            ) : (
                <div
                    className="border-2 border-[color:var(--dh-line)] bg-white overflow-hidden"
                    data-testid="emails-table"
                >
                    {/* Header (desktop) */}
                    <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-[color:var(--dh-ink)] text-white text-xs font-bold uppercase tracking-wide">
                        <div className="col-span-3">Recipient</div>
                        <div className="col-span-4">Subject</div>
                        <div className="col-span-2">Sent</div>
                        <div className="col-span-3">Delivered / Opened</div>
                    </div>
                    <ul>
                        {items.map((row, idx) => (
                            <li
                                key={row.id || row.provider_message_id || idx}
                                className="border-t border-neutral-200 first:border-t-0 px-4 py-3 md:grid md:grid-cols-12 md:gap-4 md:items-start"
                                data-testid={`email-row-${idx}`}
                            >
                                <div className="md:col-span-3 min-w-0">
                                    <p
                                        className="font-bold text-sm truncate"
                                        data-testid={`email-name-${idx}`}
                                    >
                                        {row.customer_name || "—"}
                                    </p>
                                    <p
                                        className="text-xs text-neutral-500 font-mono truncate"
                                        data-testid={`email-recipient-${idx}`}
                                    >
                                        {row.recipient}
                                    </p>
                                </div>
                                <div className="md:col-span-4 min-w-0 mt-2 md:mt-0">
                                    <p
                                        className="text-sm break-words"
                                        data-testid={`email-subject-${idx}`}
                                    >
                                        {row.subject}
                                    </p>
                                    {row.stage && (
                                        <p className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">
                                            {row.stage}
                                        </p>
                                    )}
                                </div>
                                <div
                                    className="md:col-span-2 text-xs text-neutral-700 font-mono mt-2 md:mt-0"
                                    data-testid={`email-sent-${idx}`}
                                >
                                    {formatDT(row.sent_at)}
                                    {row.status === "failed" && (
                                        <span
                                            className="ml-2 inline-block text-red-700 font-bold"
                                            data-testid={`email-failed-${idx}`}
                                        >
                                            FAILED
                                        </span>
                                    )}
                                    {row.status === "bounced" && (
                                        <span className="ml-2 inline-block text-red-700 font-bold">
                                            BOUNCED
                                        </span>
                                    )}
                                </div>
                                <div className="md:col-span-3 mt-2 md:mt-0 space-y-1">
                                    <StatusTick
                                        label="Delivered"
                                        at={row.delivered_at}
                                        testid={`email-delivered-${idx}`}
                                    />
                                    <StatusTick
                                        label="Opened"
                                        at={row.opened_at}
                                        doubleTick
                                        testid={`email-opened-${idx}`}
                                    />
                                    {row.clicked_at && (
                                        <div
                                            className="flex items-start gap-2 text-blue-700 font-bold"
                                            data-testid={`email-clicked-${idx}`}
                                        >
                                            <CheckCheck size={16} />
                                            <span className="text-xs uppercase tracking-wide">
                                                Clicked
                                            </span>
                                            <span className="text-xs text-neutral-500 font-mono leading-4">
                                                {formatDT(row.clicked_at)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default EmailsSent;
